import React from "react"
import { useTranslation } from "react-i18next"

import * as UserApi from "../../api/UserApi"

import { User, UserData, ErrorResponse } from "models"
import { useForm, useAlert } from "hooks"
import { formStatusEnum } from "components/Form/formUtils"
import {
	ACCEPTED_NOTIFICATION,
	countriesList,
	isNotEmpty,
	isPostalCodeValid,
	jsonHash,
} from "utils"

import { Spinner, Button } from "components/Elements"
import { FormElement } from "components/Form"

type UserAddressPropsProps = {
	userData: Partial<User & UserData>
	refreshUser: () => Promise<void>
}

export const UserAddress: React.FC<UserAddressPropsProps> = ({ userData, refreshUser }) => {
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
		discardChanges,
	} = useForm({
		defaultFormFields: userData,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const formHasChanged = jsonHash(userData) !== jsonHash(formFields)

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const userAddress = formFields.description.address

				// ----------------- ADDRESS UPDATE --------------------------
				// -----------------------------------------------------------
				const updateUserAddress = {
					street_name: userAddress.street_name,
					postal_code: userAddress.postal_code,
					city: userAddress.city,
					country: userAddress.country,
					// Do not send these fields if empty
					...(() => {
						if (userAddress.street_number !== "") {
							return {
								street_number: userAddress.street_number,
							}
						}
					})(),
					...(() => {
						if (userAddress.insee_code !== "") {
							return {
								insee_code: userAddress.insee_code,
							}
						}
					})(),
				}

				const updateUserDescription = {
					...formFields.description,
					address: updateUserAddress,
				}

				const updateUserData = {
					account_id: formFields.account_id,
					row_version: formFields.row_version,
					description: updateUserDescription,
					...(() => {
						if (formFields.notifications.operator_notifications_accepted_at) {
							return {
								operator_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
					...(() => {
						if (formFields.notifications.ocode_notifications_accepted_at) {
							return {
								ocode_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
				}

				const { getError } = await UserApi.updateUser(updateUserData)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				await refreshUser()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<div className="c-block px-4 lg:px-8 py-8 md:py-7">
			<h2 className="c-block__title text-2xl -top-6">{t("my_address", { ns: "user" })}</h2>

			{formFields && (
				<form>
					<div className="flex justify-between flex-wrap">
						<FormElement
							{...formElementCommonProps}
							value={formFields.description?.address?.street_number}
							name="description.address.street_number"
							type="text"
							label={t("address_number", {
								ns: "user",
							})}
							className="mb-2 md:mt-2 px-1 w-full sm:w-1/4"
							readonly={isReadonly}
						/>

						<FormElement
							{...formElementCommonProps}
							value={formFields.description?.address?.street_name}
							name="description.address.street_name"
							type="text"
							label={t("address_name", { ns: "user" })}
							className="my-2 px-1 w-full sm:w-3/4"
							readonly={isReadonly}
							required={true}
						/>
					</div>

					<div className="flex justify-between flex-wrap">
						{/* Code postal */}
						<FormElement
							{...formElementCommonProps}
							value={formFields.description?.address?.postal_code}
							name="description.address.postal_code"
							type="text"
							label={t("postal_code", { ns: "user" })}
							className="my-2 w-full sm:w-2/4 px-1"
							readonly={isReadonly}
							customRules={[
								{
									key: "postalCode-valid",
									check: (value: string) =>
										isNotEmpty(value) ? isPostalCodeValid(value) : true,
									message: t("error_invalid_value", { ns: "form" }),
								},
							]}
							required={true}
						/>

						{/* Ville */}
						<FormElement
							{...formElementCommonProps}
							value={formFields.description?.address?.city}
							name="description.address.city"
							inseeCodeName="description.address.insee_code"
							postCode={formFields.description?.address?.postal_code}
							type="city"
							id="city"
							label={t("city", { ns: "user" })}
							className="my-2 w-full sm:w-2/4 px-1"
							readonly={isReadonly}
							required={true}
						/>
					</div>

					<FormElement
						{...formElementCommonProps}
						value={formFields.description?.address?.country}
						options={countriesList}
						name="description.address.country"
						id="country"
						type="select"
						label={t("country", { ns: "user" })}
						className="my-2 md:mb-6 px-1"
						disabled={isReadonly}
						required={true}
					/>

					{isReadonly && (
						<Button
							variant="primary"
							type="submit"
							onClick={() => setFormStatus(formStatusEnum.ready)}
							className="px-10 py-1 mt-10"
							size="small"
						>
							{t("global.edit")}
						</Button>
					)}

					{isReady && (
						<div className="flex items-center mt-10 justify-center md:justify-start">
							<Button
								variant="secondary"
								type="submit"
								onClick={handleSubmit}
								className="mx-1 px-10 py-1 lg:mx-3"
								disabled={!formHasChanged}
								size="small"
							>
								{t("global.validate")}
							</Button>
							<Button
								variant="secondaryBordered"
								type="button"
								onClick={() =>
									discardChanges({ formHasChanged, refreshData: refreshUser })
								}
								className="mx-1 px-10 py-1 lg:mx-3"
								size="small"
							>
								{t("global.cancel")}
							</Button>
						</div>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}
				</form>
			)}
		</div>
	)
}
